import { create } from 'zustand';
import api from '../lib/api';

interface Incident {
    id: string;
    title: string;
    description: string;
    severity: string;
    status: string;
    createdAt: string;
}

interface IncidentMetrics {
    activeCritical: number;
    mttr: string;
    uptime: number;
}

interface IncidentState {
    incidents: Incident[];
    metrics: IncidentMetrics | null;
    loading: boolean;
    error: string | null;
    fetchIncidents: () => Promise<void>;
    fetchMetrics: () => Promise<void>;
    createIncident: (data: any) => Promise<void>;
    updateIncident: (id: string, data: any) => Promise<void>;
    deleteIncident: (id: string) => Promise<void>;
}

export const useIncidentStore = create<IncidentState>((set) => ({
    incidents: [],
    metrics: null,
    loading: false,
    error: null,

    fetchIncidents: async () => {
        set({ loading: true, error: null });
        try {
            const response = await api.get('/work/incidents');
            set({ incidents: response.data, loading: false });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    fetchMetrics: async () => {
        set({ loading: true, error: null });
        try {
            const response = await api.get('/work/incidents/metrics');
            set({ metrics: response.data, loading: false });
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    createIncident: async (data: any) => {
        set({ loading: true, error: null });
        try {
            const response = await api.post('/work/incidents', data);
            set(state => ({ 
                incidents: [response.data, ...state.incidents],
                loading: false 
            }));
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    updateIncident: async (id: string, data: any) => {
        set({ loading: true, error: null });
        try {
            const response = await api.patch(`/work/incidents/${id}`, data);
            set(state => ({
                incidents: state.incidents.map(i => i.id === id ? response.data : i),
                loading: false
            }));
        } catch (error: any) {
            set({ error: error.message, loading: false });
        }
    },

    deleteIncident: async (id: string) => {
        try {
            await api.delete(`/work/incidents/${id}`);
            set(state => ({ incidents: state.incidents.filter(i => i.id !== id) }));
        } catch (error: any) {
            set({ error: error.message });
            throw error;
        }
    }
}));
